"use client";

import { useEffect, useState } from 'react';
import { locales as allLocales, getLocaleFromTimezone, type Locale } from './i18n';

/**
 * Locale list for the intl (ar/en/de/tr) language switcher. fa is dropped
 * unless the visitor's browser timezone maps to fa (Iran/Afghanistan/
 * Tajikistan — same set TimezoneLocaleSync uses). Driven purely by
 * timezone: this hook only ever mounts on ar/en/de/tr pages (fa has its
 * own separate Header/Footer, never this switcher), so there's no "the
 * current page is fa" case to special-case — and no manual-pick check
 * either, since picking any *other* locale from here shouldn't leave fa
 * stuck visible from an earlier landing.
 *
 * This only hides the nav link — routing, sitemap.ts and the hreflang
 * `alternates.languages` map in lib/metadata.ts keep listing fa
 * unconditionally, so GSC still crawls/indexes /fa/... pages and the
 * hreflang cluster stays intact. Direct navigation to /fa/... is untouched.
 *
 * Starts from the full list (matches server render, avoids hydration
 * mismatch) and narrows client-side after mount.
 */
export function useSwitcherLocales(): Locale[] {
    const [locales, setLocales] = useState<Locale[]>(allLocales);

    useEffect(() => {
        let timeZone: string;
        try {
            timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch {
            return;
        }

        setLocales(getLocaleFromTimezone(timeZone) === 'fa' ? allLocales : allLocales.filter((l) => l !== 'fa'));
    }, []);

    return locales;
}
